// src/tabs/HomeTab.jsx
import React, { useState, useMemo } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import Overlay from "../components/Overlay";

// ---- DATA 1: TRADITIONS (15 items) ----
const TRADITIONS = [
  {
    title: "Tree night",
    movie: "Elf",
    summary: "Pick a theme & play the first song of the season.",
    why: "Kicks off the season with shared excitement and a ritual soundtrack.",
    supplies: ["ornaments", "tree lights", "speaker/playlist"],
    steps: [
      "Choose a theme (color, year, movie).",
      "Play the 'first song' to start decorating.",
      "Take a yearly family photo by the lit tree.",
    ],
  },
  {
    title: "Cookie swap",
    movie: "The Holiday",
    summary: "Everyone bakes a favorite; make a plate for neighbors.",
    why: "Tradable treats + neighborly moment.",
    supplies: ["recipes", "ingredients", "tins/plates", "labels"],
    steps: [
      "Bake or buy favorites.",
      "Swap boxes; label allergens.",
      "Drop a plate at a neighbor’s door.",
    ],
  },
  {
    title: "Lights drive",
    movie: "Christmas Chronicles",
    summary: "Vote Top 3 houses; leave a thank-you note.",
    why: "Low-prep wonder outside the house.",
    supplies: ["cocoa in thermos", "thank-you notes", "playlist"],
    steps: [
      "Map 2–3 streets.",
      "Rate each house 1–5✨.",
      "Leave a thank-you at the winner.",
    ],
  },
  {
    title: "Snowflake notes",
    movie: "Klaus",
    summary: "Write kind notes on paper snowflakes; hang up.",
    why: "Turns kindness into visible decor.",
    supplies: ["white paper", "scissors", "tape", "pens"],
    steps: [
      "Cut snowflakes together.",
      "Write a kind note per flake.",
      "Hang along a doorway or window.",
    ],
  },
  {
    title: "Hot cocoa station",
    movie: "Polar Express",
    summary: "Set up toppings bar: mallows, candy cane stirrers.",
    why: "Warm and customizable treat ritual.",
    supplies: ["cocoa", "mugs", "marshmallows", "peppermint", "cinnamon"],
    steps: [
      "Arrange toppings.",
      "Assign cocoa captain.",
      "Photo the best mug build.",
    ],
  },
  {
    title: "Home Alone traps (safe!)",
    movie: "Home Alone",
    summary: "Design funny—but safe—'booby-trap' Rube Goldberg.",
    why: "Creative engineering + giggles.",
    supplies: ["dominoes", "string", "cardboard tubes", "tape"],
    steps: [
      "Sketch a chain-reaction idea.",
      "Build in a clear safe area.",
      "Film the run—bloopers allowed.",
    ],
  },
  {
    title: "Grinch heart grow",
    movie: "How the Grinch Stole Christmas",
    summary: "Do one sneaky kindness for someone in the house.",
    why: "Secret kindness builds delight.",
    supplies: ["green paper hearts", "pen"],
    steps: [
      "Pick a person secretly.",
      "Do one small kindness.",
      "Add a heart to the fridge tally.",
    ],
  },
  {
    title: "Angel tree tag",
    movie: "Miracle on 34th Street",
    summary: "Choose a giving tree tag; shop together.",
    why: "Tangible generosity routine.",
    supplies: ["tag from local drive", "budget", "wrapping"],
    steps: ["Pick a tag.", "Shop as a team.", "Wrap & drop off on time."],
  },
  {
    title: "Bell of thanks",
    movie: "It's a Wonderful Life",
    summary: "Ring a bell after naming a gratitude.",
    why: "Builds a simple nightly rhythm.",
    supplies: ["small bell"],
    steps: [
      "After dinner, pass the bell.",
      "Each shares 1 gratitude → ring.",
      "Hang bell on tree.",
    ],
  },
  {
    title: "Pajama parade",
    movie: "A Christmas Story",
    summary: "New PJs + goofy living-room catwalk.",
    why: "Silly tradition that marks the season.",
    supplies: ["PJs", "camera", "music"],
    steps: ["Unveil PJs.", "Run the parade.", "Crown Best Twirl/Coziest."],
  },
  {
    title: "Window paper silhouettes",
    movie: "The Santa Clause",
    summary: "Cut silhouettes (reindeer, sleigh) for windows.",
    why: "Cozy curb appeal + crafts.",
    supplies: ["black craft paper", "scissors", "tape"],
    steps: [
      "Trace simple shapes.",
      "Cut and tape to window.",
      "Backlight at dusk.",
    ],
  },
  {
    title: "Carol call",
    movie: "Love Actually",
    summary: "FaceTime grandparents & sing one verse.",
    why: "Connects generations with laughter.",
    supplies: ["lyrics on phone", "call list"],
    steps: ["Pick a carol.", "Place the call.", "One-take performance."],
  },
  {
    title: "Night of giving back",
    movie: "Noelle",
    summary: "Assemble care kits (socks, snacks, notes).",
    why: "Hands-on service with kids.",
    supplies: ["zip bags", "socks", "granola bars", "notes"],
    steps: [
      "Lay out stations.",
      "Assemble 5–10 kits.",
      "Deliver together or to a shelter.",
    ],
  },
  {
    title: "Snow globe memory",
    movie: "Jingle All the Way",
    summary: "Write one tiny memory; jar becomes a 'snow globe'.",
    why: "Micro-journaling that accumulates meaning.",
    supplies: ["jar", "paper slips", "pen", "glitter (optional)"],
    steps: [
      "After an outing, write 1 line.",
      "Fold & drop in jar.",
      "Read them New Year’s Day.",
    ],
  },
  {
    title: "Charlie Brown tree rescue",
    movie: "A Charlie Brown Christmas",
    summary: "Find the scraggliest branch & revive it with love.",
    why: "Teaches care > perfection.",
    supplies: ["small branch", "tiny stand/cup", "one ornament"],
    steps: [
      "Choose the branch.",
      "Stabilize in cup/stand.",
      "Add 1 ornament & a spotlight.",
    ],
  },
];

// ---- DATA 2: DAILY IDEAS (10 items) ----
const IDEAS = [
  {
    cat: "Outing",
    title: "Drive to see neighborhood lights",
    tip: "Vote on Top 3 houses; leave a thank-you note.",
    why: "Shared wonder + a relaxing drive playlist.",
    supplies: ["thermos of cocoa", "phone for photos", "thank-you notes"],
    duration: "60m",
    cost: "Free",
    age: "All",
    steps: [
      "Pick 2–3 nearby streets.",
      "Family playlist; windows cracked.",
      "Vote Top 3; drop a note at the favorite.",
    ],
  },
  {
    cat: "Outing",
    title: "Town square tree + cocoa",
    tip: "Bring travel mugs & a playlist.",
    why: "Low-prep outing, big seasonal vibes.",
    supplies: ["travel mugs", "cocoa mix", "playlist"],
    duration: "60m",
    cost: "$",
    age: "All",
    steps: [
      "Drive to town square.",
      "Three photos: tree, group, candid.",
      "Warm up with cocoa in the car.",
    ],
  },
  {
    cat: "Home",
    title: "Cookie bake-off",
    tip: "Each person decorates 3 cookies; photo the winners.",
    why: "Friendly competition + delicious reward.",
    supplies: ["cookie dough", "cookie cutters", "icing", "sprinkles"],
    duration: "60m",
    cost: "$$",
    age: "All",
    steps: [
      "Bake a batch together.",
      "10-min decorate timer.",
      "Vote Best Look, Best Flavor, Silliest.",
    ],
  },
  {
    cat: "Mindful",
    title: "Gratitude circle",
    tip: "One sentence each about the day.",
    why: "Trains attention toward good moments; quick & restorative.",
    duration: "15m",
    cost: "Free",
    age: "All",
    steps: [
      "Sit together.",
      "One specific thing from today per person.",
      "Optional ‘gratitude selfie’.",
    ],
  },
  {
    cat: "Give-back",
    title: "Donate toys/food",
    tip: "Kids choose the items & why.",
    why: "Builds agency and generosity.",
    duration: "30m",
    cost: "$",
    age: "All",
    steps: [
      "Pick a cause/store.",
      "Kids choose 1–3 items each.",
      "Deliver together and reflect on why.",
    ],
  },
  {
    cat: "Game",
    title: "Candy cane scavenger",
    tip: "Timer + zones by age.",
    why: "Active fun that scales to any house.",
    supplies: ["mini candy canes"],
    duration: "15m",
    cost: "$",
    age: "All",
    steps: [
      "One person hides by zone.",
      "Timers by age; youngest head start.",
      "Winners share finds; candy for all.",
    ],
  },
  {
    cat: "Craft",
    title: "DIY ornament",
    tip: "Label with the year for keepsake.",
    why: "Creates a physical memory you’ll unbox every year.",
    supplies: ["clear balls", "paint/markers", "ribbon", "date labels"],
    duration: "30m",
    cost: "$",
    age: "All",
    steps: [
      "Cover table; set out supplies.",
      "Design one ornament with name + year.",
      "Hang on tree; quick photo.",
    ],
  },
  {
    cat: "Create",
    title: "Record a carol",
    tip: "One take only—embrace bloopers.",
    why: "Captures your family’s sound this year.",
    supplies: ["phone for video", "lyrics on screen"],
    duration: "30m",
    cost: "Free",
    age: "All",
    steps: [
      "Pick a short carol.",
      "Prop phone at eye level.",
      "One take; share with grandparents.",
    ],
  },
  {
    cat: "Music",
    title: "Family playlist",
    tip: "Each adds 2 classics + 1 new hit.",
    why: "Blends nostalgia with discovery.",
    duration: "30m",
    cost: "Free",
    age: "All",
    steps: [
      "Everyone adds songs.",
      "Name the playlist for this year.",
      "Play on drives and at dinner.",
    ],
  },
  {
    cat: "Food",
    title: "Gingerbread house night",
    tip: "Hot glue for structure; icing for fun.",
    why: "Low-stress builds = more fun decorating.",
    supplies: ["kits", "candy", "icing"],
    duration: "60m",
    cost: "$$",
    age: "All",
    steps: [
      "Pre-assemble with glue (optional).",
      "Decorate freely; photo each house.",
      "Vote for fun awards.",
    ],
  },
];

export default function HomeTab({ onOpenAlbums }) {
  const heroImg =
    "https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=1800&auto=format&fit=crop";

  const PROMPTS = [
    "Which line is Dad today?",
    "What doorway does this verse open for us?",
    "Who were we when we first heard this?",
  ];

  const [idx, setIdx] = useState(() =>
    Math.floor(Math.random() * PROMPTS.length)
  );
  const [shuffleKey, setShuffleKey] = useState(0);

  // Controls which item (Tradition or Idea) is open in the modal
  const [activeItem, setActiveItem] = useState(null);

  // Merged and shuffled list of everything for the main grid
  const mixedInspiration = useMemo(() => {
    // Combine both lists, tagging them so we know which is which
    const combined = [
      ...TRADITIONS.map((t) => ({ ...t, type: "tradition" })),
      ...IDEAS.map((i) => ({ ...i, type: "idea" })),
    ];
    return combined.sort(() => 0.5 - Math.random());
  }, [shuffleKey]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* 1. Hero Section */}
      <section
        className="card lg"
        style={{ padding: 0, overflow: "hidden" }}
        aria-label="Arriving here"
      >
        <div style={{ position: "relative" }}>
          <img
            src={heroImg}
            alt=""
            style={{
              width: "100%",
              height: 260,
              objectFit: "cover",
              filter: "grayscale(.2) contrast(1.05)",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(28,26,22,.35), rgba(28,26,22,.75))",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              padding: 24,
              color: "#fff",
            }}
          >
            <div>
              <div
                className="pill"
                style={{
                  background: "rgba(255,255,255,.1)",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,.4)",
                }}
              >
                Dylan · Family Songbook
              </div>
              <h1 className="h1" style={{ marginTop: 8, color: "#fff" }}>
                A place for the songs — and what they meant at our table.
              </h1>
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Button variant="secondary" onClick={onOpenAlbums}>
                  Browse albums
                </Button>
                <Button
                  className="ghost"
                  onClick={() => setIdx((i) => (i + 1) % PROMPTS.length)}
                  style={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.3)",
                  }}
                >
                  New prompt
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Today's Question */}
      <Card className="lg paper">
        <div className="h2" style={{ marginBottom: 6 }}>
          Today’s Question
        </div>
        <p className="sm" style={{ margin: 0 }}>
          {PROMPTS[idx]}
        </p>
      </Card>

      {/* 3. Holiday Inspiration Grid */}
      <section>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <div className="h2" style={{ fontSize: 22 }}>
              Holiday Inspiration
            </div>
            <div className="sm muted">
              Traditions & daily ideas mixed together.
            </div>
          </div>
          <Button
            className="ghost"
            onClick={() => setShuffleKey((k) => k + 1)}
            style={{ fontSize: 13 }}
          >
            Shuffle ↻
          </Button>
        </div>

        <div
          className="grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {mixedInspiration.map((item, i) => (
            <button
              key={`${item.title}-${i}`}
              onClick={() => setActiveItem(item)}
              className="card"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                height: "100%",
                padding: 20,
                textAlign: "left",
                cursor: "pointer",
                transition: "transform 0.1s ease",
                background: "white",
                borderLeft:
                  item.type === "idea"
                    ? "4px solid var(--brand)"
                    : "4px solid var(--accent)",
              }}
              onMouseDown={(e) =>
                (e.currentTarget.style.transform = "scale(0.98)")
              }
              onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 8,
                  width: "100%",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  {item.title}
                </div>
                {/* Tag Pill */}
                <span
                  className="pill"
                  style={{
                    fontSize: 10,
                    padding: "2px 8px",
                    background: item.type === "idea" ? "#d1fae5" : "#fef3c7",
                    color: item.type === "idea" ? "#065f46" : "#92400e",
                    border: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.cat || item.movie || "Tradition"}
                </span>
              </div>
              <p
                className="sm muted"
                style={{ margin: 0, flex: 1, lineHeight: 1.5 }}
              >
                {item.summary || item.tip}
              </p>
              <div
                className="xs"
                style={{
                  marginTop: 8,
                  color: "var(--ink70)",
                  fontStyle: "italic",
                  borderTop: "1px dashed var(--line)",
                  paddingTop: 8,
                  width: "100%",
                }}
              >
                {item.why}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* 4. Details Overlay */}
      {activeItem && (
        <Overlay
          open={!!activeItem}
          onClose={() => setActiveItem(null)}
          title={activeItem.title}
        >
          <div style={{ padding: 16 }}>
            {/* Header / Meta */}
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 20,
                flexWrap: "wrap",
              }}
            >
              <span className="pill">
                {activeItem.type === "idea" ? "Daily Idea" : "Tradition"}
              </span>
              {activeItem.duration && (
                <span className="pill">⏱ {activeItem.duration}</span>
              )}
              {activeItem.cost && (
                <span className="pill">💵 {activeItem.cost}</span>
              )}
              {activeItem.age && (
                <span className="pill">👪 {activeItem.age}</span>
              )}
            </div>

            <p
              className="sm"
              style={{ marginBottom: 20, fontSize: 16, color: "var(--ink)" }}
            >
              {activeItem.summary || activeItem.tip}
            </p>

            <div className="grid" style={{ gap: 20 }}>
              {/* Why it matters */}
              <div
                style={{
                  background: "var(--bg)",
                  padding: 12,
                  borderRadius: 8,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                  Why it's special:
                </div>
                <div className="sm">{activeItem.why}</div>
              </div>

              {/* Supplies */}
              {activeItem.supplies && (
                <div>
                  <div className="h2" style={{ fontSize: 16, marginBottom: 8 }}>
                    Supplies
                  </div>
                  <ul className="sm" style={{ paddingLeft: 20, margin: 0 }}>
                    {activeItem.supplies.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Steps (if available) */}
              {activeItem.steps && (
                <div>
                  <div className="h2" style={{ fontSize: 16, marginBottom: 8 }}>
                    How to do it
                  </div>
                  <ol className="sm" style={{ paddingLeft: 20, margin: 0 }}>
                    {activeItem.steps.map((s) => (
                      <li key={s} style={{ marginBottom: 4 }}>
                        {s}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <Button onClick={() => setActiveItem(null)}>Close</Button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}
